import { useState, useCallback, useRef } from 'react';
import type { PageInfo, PageState, SerializedToken, SessionState, LocationLink } from '@roll1/shared';

export interface UsePagesReturn {
  pages: PageInfo[];
  activePageId: string;
  viewingPageId: string;
  isPreview: boolean;
  navigationMode: 'free' | 'confirm' | 'gm-only';

  /** Populate from v2 SessionState on join. Returns the active page data. */
  initFromState: (state: SessionState) => PageState | null;

  /** Read/write page data in the store */
  getPageData: (pageId: string) => PageState | null;
  setPageData: (pageId: string, data: PageState) => void;

  /** Server message handlers */
  handlePageList: (pages: PageInfo[], activePageId: string) => void;
  handlePageCreated: (page: PageInfo) => void;
  handlePageRenamed: (pageId: string, name: string) => void;
  handlePageDeleted: (pageId: string) => void;
  /** Returns the new viewingPageId (always follows active — FR-008a) */
  handlePageActivated: (pageId: string, pageState: PageState) => string;
  handlePageReordered: (pageIds: string[]) => void;
  handleTokensTransferred: (
    sourcePageId: string,
    targetPageId: string,
    tokenIds: string[],
    tokens: SerializedToken[],
  ) => void;

  /** Location link handlers */
  handleLocationLinkCreated: (pageId: string, link: LocationLink) => void;
  handleLocationLinkUpdated: (pageId: string, link: LocationLink) => void;
  handleLocationLinkDeleted: (pageId: string, linkId: string) => void;
  handleMapChanged: (pageId: string, url: string | null) => void;
  handleNavigationModeChanged: (mode: 'free' | 'confirm' | 'gm-only') => void;

  /** Build breadcrumb path from root to given pageId */
  getBreadcrumb: (pageId: string) => PageInfo[];

  /** GM Preview */
  previewPage: (pageId: string) => void;
  returnToActive: () => void;

  /** Direct setter (used during page switch orchestration) */
  setViewingPageId: React.Dispatch<React.SetStateAction<string>>;
}

export function usePages(): UsePagesReturn {
  const [pages, setPages] = useState<PageInfo[]>([]);
  const [activePageId, setActivePageId] = useState('');
  const [viewingPageId, setViewingPageId] = useState('');
  const [navigationMode, setNavigationMode] = useState<'free' | 'confirm' | 'gm-only'>('confirm');
  const store = useRef<Record<string, PageState>>({});

  const isPreview = viewingPageId !== '' && viewingPageId !== activePageId;

  const getPageData = useCallback((pageId: string): PageState | null => {
    return store.current[pageId] ?? null;
  }, []);

  const setPageData = useCallback((pageId: string, data: PageState) => {
    store.current[pageId] = data;
  }, []);

  /* ---- initialise from v2 server state ---- */
  const initFromState = useCallback((state: SessionState): PageState | null => {
    store.current = {};
    for (const p of state.pages) store.current[p.id] = p;

    const infos: PageInfo[] = state.pages.map(p => ({
      id: p.id,
      name: p.name,
      sortOrder: p.sortOrder,
      isActive: p.id === state.activePageId,
      tokenCount: p.tokens.length,
      hasMap: p.mapUrl !== null,
      parentPageId: p.parentPageId,
    }));
    setPages(infos);
    setActivePageId(state.activePageId);
    setViewingPageId(state.activePageId);
    setNavigationMode(state.navigationMode ?? 'confirm');

    return store.current[state.activePageId] ?? null;
  }, []);

  /* ---- server message handlers ---- */

  const handlePageList = useCallback((newPages: PageInfo[], newActiveId: string) => {
    setPages(newPages);
    setActivePageId(newActiveId);
  }, []);

  const handlePageCreated = useCallback((page: PageInfo) => {
    setPages(prev => [...prev, page]);
    if (!store.current[page.id]) {
      store.current[page.id] = {
        id: page.id,
        name: page.name,
        sortOrder: page.sortOrder,
        mapUrl: null,
        gridSize: 70,
        tokens: [],
        drawings: [],
        fog: { enabled: false, mode: 'manual', revealed: [], closings: [], explored: [] },
        parentPageId: page.parentPageId,
        locationLinks: [],
        notes: [],
        pins: [],
        annotations: [],
        triggers: [],
        textObjects: [],
        mapObjects: [],
        walls: [],
        globalIllumination: { level: 'bright' },
      };
    }
  }, []);

  const handlePageRenamed = useCallback((pageId: string, name: string) => {
    setPages(prev => prev.map(p => (p.id === pageId ? { ...p, name } : p)));
    const d = store.current[pageId];
    if (d) d.name = name;
  }, []);

  const handlePageDeleted = useCallback((pageId: string) => {
    setPages(prev => prev.filter(p => p.id !== pageId));
    delete store.current[pageId];
  }, []);

  const handlePageActivated = useCallback(
    (pageId: string, pageState: PageState): string => {
      setActivePageId(pageId);
      setPages(prev => prev.map(p => ({ ...p, isActive: p.id === pageId })));
      store.current[pageId] = pageState;
      // FR-008a: auto-exit Preview when Ribbon changes
      setViewingPageId(pageId);
      return pageId;
    },
    [],
  );

  const handlePageReordered = useCallback((pageIds: string[]) => {
    setPages(prev => {
      const mapped = new Map(prev.map(p => [p.id, p]));
      return pageIds
        .map((id, i) => {
          const p = mapped.get(id);
          return p ? { ...p, sortOrder: i } : null;
        })
        .filter((p): p is PageInfo => p !== null);
    });
  }, []);

  const handleTokensTransferred = useCallback(
    (
      sourcePageId: string,
      targetPageId: string,
      tokenIds: string[],
      tokens: SerializedToken[],
    ) => {
      const src = store.current[sourcePageId];
      if (src) src.tokens = src.tokens.filter(t => !tokenIds.includes(t.id));
      const tgt = store.current[targetPageId];
      if (tgt) tgt.tokens.push(...tokens);
      setPages(prev =>
        prev.map(p => {
          if (p.id === sourcePageId && src) return { ...p, tokenCount: src.tokens.length };
          if (p.id === targetPageId && tgt) return { ...p, tokenCount: tgt.tokens.length };
          return p;
        }),
      );
    },
    [],
  );

  /* ---- GM Preview ---- */

  const previewPage = useCallback((pageId: string) => {
    setViewingPageId(pageId);
  }, []);

  const returnToActive = useCallback(() => {
    setViewingPageId(prev => {
      // use functional update accessing activePageId via ref-style trick
      return activePageId;
    });
  }, [activePageId]);

  /* ---- Location link handlers ---- */

  const handleLocationLinkCreated = useCallback((pageId: string, link: LocationLink) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = [...pageData.locationLinks, link];
    }
    // Update parentPageId on the target page info if not set
    setPages(prev => prev.map(p => {
      if (p.id === link.targetPageId && p.parentPageId === null) {
        const targetData = store.current[p.id];
        if (targetData) targetData.parentPageId = pageId;
        return { ...p, parentPageId: pageId };
      }
      return p;
    }));
  }, []);

  const handleLocationLinkUpdated = useCallback((pageId: string, link: LocationLink) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = pageData.locationLinks.map(l => l.id === link.id ? link : l);
    }
  }, []);

  const handleLocationLinkDeleted = useCallback((pageId: string, linkId: string) => {
    const pageData = store.current[pageId];
    if (pageData) {
      pageData.locationLinks = pageData.locationLinks.filter(l => l.id !== linkId);
    }
  }, []);

  const handleNavigationModeChanged = useCallback((mode: 'free' | 'confirm' | 'gm-only') => {
    setNavigationMode(mode);
  }, []);

  /* ---- Breadcrumb ---- */

  /**
   * Build breadcrumb path from root to the given pageId.
   * Traverses parentPageId chain, stopping at depth 5 to prevent infinite cycles.
   */
  const getBreadcrumb = useCallback((pageId: string): PageInfo[] => {
    const MAX_DEPTH = 5;
    const path: PageInfo[] = [];
    const visited = new Set<string>();
    let currentId: string | null = pageId;

    while (currentId !== null && path.length < MAX_DEPTH) {
      if (visited.has(currentId)) break; // cycle detection
      visited.add(currentId);
      const info = pages.find(p => p.id === currentId);
      if (!info) break;
      path.unshift(info);
      currentId = info.parentPageId;
    }

    return path;
  }, [pages]);

  return {
    pages,
    activePageId,
    viewingPageId,
    isPreview,
    navigationMode,
    initFromState,
    getPageData,
    setPageData,
    handlePageList,
    handlePageCreated,
    handlePageRenamed,
    handlePageDeleted,
    handlePageActivated,
    handlePageReordered,
    handleTokensTransferred,
    handleLocationLinkCreated,
    handleLocationLinkUpdated,
    handleMapChanged: useCallback((pageId: string, url: string | null) => {
      // Update the mutable page store
      const pd = store.current[pageId];
      if (pd) pd.mapUrl = url;
      // Update React state so toolbar re-renders
      setPages(prev => prev.map(p => p.id === pageId ? { ...p, hasMap: url !== null } : p));
    }, []),
    handleLocationLinkDeleted,
    handleNavigationModeChanged,
    getBreadcrumb,
    previewPage,
    returnToActive,
    setViewingPageId,
  };
}
